/**
 * Simple ID obfuscation utility for frontend URLs.
 * Converts real database IDs to/from dummy values.
 * Security note: This is cosmetic obfuscation only, not cryptographic.
 * The real ID is still stored internally and sent to the API.
 */

const SEED = 9973; // Prime number for basic hashing

/**
 * Encode real database ID to obfuscated URL-safe value
 * @param {number} realId - Real database ID
 * @returns {string} - Obfuscated ID string (alphanumeric)
 */
export function encodeId(realId) {
  if (!realId) return '';
  // Multiply by prime, add random offset, convert to base36
  const encoded = ((realId * SEED) ^ 0x12345678).toString(36);
  return encoded;
}

/**
 * Decode obfuscated URL value back to real database ID
 * @param {string} encodedId - Obfuscated ID from URL
 * @returns {number} - Real database ID
 */
export function decodeId(encodedId) {
  if (!encodedId) return null;
  try {
    // Reverse the process: convert from base36, XOR back, divide by prime
    const decoded = parseInt(encodedId, 36) ^ 0x12345678;
    const realId = decoded / SEED;
    // Return integer if the result is valid
    return Number.isInteger(realId) ? realId : null;
  } catch (err) {
    return null;
  }
}

/**
 * Check if a value looks like an encoded ID
 * @param {string} value - Value to check
 * @returns {boolean}
 */
export function isEncodedId(value) {
  // Simple heuristic: encoded IDs are short alphanumeric strings
  return /^[a-z0-9]{4,12}$/.test(String(value).toLowerCase());
}
